import { collection, doc, getDocs, getDoc, addDoc, updateDoc, deleteDoc, query, where, orderBy, collectionGroup, onSnapshot } from 'firebase/firestore';
import { db } from '../../firebase';
import { getAuth } from 'firebase/auth';
import { triggerNewFireNotification } from '../notificationTriggerService';

const FIRES_COLLECTION = 'fires';

// Get all fires
export const getAllFires = async () => {
  try {
    const firesRef = collection(db, FIRES_COLLECTION);
    const q = query(firesRef, orderBy('dateReported', 'desc'));
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Error getting fires:', error);
    throw error;
  }
};

// Get a single fire by ID
export const getFireById = async (id) => {
  try {
    const fireRef = doc(db, FIRES_COLLECTION, id);
    const fireDoc = await getDoc(fireRef);
    
    if (fireDoc.exists()) {
      const fireData = fireDoc.data();
      
      // Get notes from subcollection
      const notesRef = collection(fireRef, 'notes');
      const notesSnapshot = await getDocs(notesRef);
      const notes = notesSnapshot.docs.map(noteDoc => ({
        id: noteDoc.id,
        ...noteDoc.data()
      }));

      return {
        id: fireDoc.id,
        ...fireData,
        notes: notes
      };
    }
    return null;
  } catch (error) {
    console.error('Error getting fire:', error);
    throw error;
  }
};

// Create a new fire
export const createFire = async (fireData) => {
  try {
    const firesRef = collection(db, FIRES_COLLECTION);
    const docRef = await addDoc(firesRef, {
      name: fireData.name,
      companyName: fireData.companyName || fireData.accountName || 'Unknown Company',
      description: fireData.description || '',
      assignedTo: fireData.assignedTo || [],
      status: fireData.status || 'Active',
      deadline: fireData.deadline || null,
      dateReported: fireData.dateReported || new Date().toISOString(),
      accountId: fireData.accountId || null, // Handle undefined accountId for internal fires
      isInternal: fireData.isInternal || false,
      createdBy: fireData.createdBy || "Current User",
      createdByName: fireData.createdByName || "Current User",
      createdAt: new Date().toISOString(),
      notes: [] // Initialize empty notes array
    });
    
    console.log('✅ Fire created successfully:', docRef.id);
    
    // Trigger new fire notification
    try {
      await triggerNewFireNotification({
        fireName: fireData.name,
        description: fireData.description,
        severity: fireData.severity || 'High',
        reporter: fireData.createdByName || fireData.createdBy || 'Unknown',
        assignedTo: fireData.assignedTo,
        companyName: fireData.companyName || fireData.accountName,
        accountId: fireData.accountId || null, // Handle undefined accountId for internal fires
        isInternal: fireData.isInternal,
        deadline: fireData.deadline,
        timestamp: new Date().toISOString()
      });
      console.log('✅ New fire notification sent successfully');
    } catch (notificationError) {
      console.error('❌ Error sending new fire notification:', notificationError);
      // Don't fail the fire creation if notification fails
    }
    
    return {
      id: docRef.id,
      ...fireData,
      notes: [] // Include empty notes array in the return object
    };
  } catch (error) {
    console.error('Error creating fire:', error);
    throw error;
  }
};

// Update a fire
export const updateFire = async (id, fireData) => {
  try {
    const fireRef = doc(db, FIRES_COLLECTION, id);
    const updateData = {};
    
    // Map the incoming data to the correct field names
    if (fireData.name) updateData.firename = fireData.name;
    if (fireData.companyName) updateData.account = fireData.companyName;
    if (fireData.assignedTo) updateData.assignedTo = fireData.assignedTo;
    if (fireData.deadline) updateData.deadline = fireData.deadline;
    if (fireData.description) updateData.description = fireData.description;
    if (fireData.status) updateData.status = fireData.status;
    
    await updateDoc(fireRef, updateData);
    return {
      id,
      ...fireData
    };
  } catch (error) {
    console.error('Error updating fire:', error);
    throw error;
  }
};

// Add a note to a fire
export const addNoteToFire = async (fireId, noteInput) => {
  try {
    const fireRef = doc(db, FIRES_COLLECTION, fireId);
    const notesRef = collection(fireRef, 'notes');

    const auth = getAuth();
    const authUser = auth?.currentUser;

    // Support both string input and object input
    const inputIsString = typeof noteInput === 'string';
    const noteText = inputIsString ? noteInput : noteInput?.noteText;
    const author = inputIsString
      ? (authUser?.displayName || authUser?.email?.split('@')[0] || 'User')
      : (noteInput?.author || authUser?.displayName || authUser?.email?.split('@')[0] || 'User');
    const authorId = inputIsString
      ? (authUser?.uid || null)
      : (noteInput?.authorId ?? authUser?.uid ?? null);
    const authorRole = inputIsString ? undefined : noteInput?.authorRole;
    const authorProfileImage = inputIsString
      ? (authUser?.photoURL || undefined)
      : (noteInput?.authorProfileImage ?? authUser?.photoURL ?? undefined);

    const newNote = {
      author,
      authorId,
      ...(authorRole ? { authorRole } : {}),
      ...(authorProfileImage ? { authorProfileImage } : {}),
      noteText,
      createdAt: new Date().toISOString()
    };

    const docRef = await addDoc(notesRef, newNote);
    return {
      id: docRef.id,
      ...newNote
    };
  } catch (error) {
    console.error('Error adding note:', error);
    throw error;
  }
};

// Update a note in a fire
export const updateNoteInFire = async (fireId, noteId, updatedNoteText) => {
  try {
    const noteRef = doc(db, FIRES_COLLECTION, fireId, 'notes', noteId);
    await updateDoc(noteRef, {
      noteText: updatedNoteText
    });
    
    const noteDoc = await getDoc(noteRef);
    return {
      id: noteDoc.id,
      ...noteDoc.data()
    };
  } catch (error) {
    console.error('Error updating note:', error);
    throw error;
  }
};

// Delete a note from a fire
export const deleteNoteFromFire = async (fireId, noteId) => {
  try {
    const noteRef = doc(db, FIRES_COLLECTION, fireId, 'notes', noteId);
    await deleteDoc(noteRef);
    
    // Get updated notes list
    const notesRef = collection(doc(db, FIRES_COLLECTION, fireId), 'notes');
    const notesSnapshot = await getDocs(notesRef);
    return notesSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Error deleting note:', error);
    throw error;
  }
};

// Delete a fire
export const deleteFire = async (id) => {
  try {
    const fireRef = doc(db, FIRES_COLLECTION, id);
    await deleteDoc(fireRef);
    return id;
  } catch (error) {
    console.error('Error deleting fire:', error);
    throw error;
  }
};

// Get fires assigned to current user
export const getFiresAssignedToCurrentUser = async () => {
  try {
    const auth = getAuth();
    if (!auth.currentUser) {
      throw new Error('User must be authenticated to get fires');
    }

    const firesRef = collection(db, FIRES_COLLECTION);
    const q = query(firesRef, orderBy('dateReported', 'desc'));
    const querySnapshot = await getDocs(q);
    
    // Filter fires where the current user is in the assignedTo array
    const allFires = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    // Filter fires where the current user is assigned
    // Check both by user ID and by display name/email for backward compatibility
    const currentUserFires = allFires.filter(fire => 
      fire.assignedTo && 
      Array.isArray(fire.assignedTo) && 
      fire.assignedTo.some(assignee => {
        // Check if assignee matches user ID
        if (assignee === auth.currentUser.uid) {
          return true;
        }
        // Check if assignee matches display name
        if (auth.currentUser.displayName && assignee.toLowerCase().includes(auth.currentUser.displayName.toLowerCase())) {
          return true;
        }
        // Check if assignee matches email
        if (auth.currentUser.email && assignee.toLowerCase().includes(auth.currentUser.email.toLowerCase())) {
          return true;
        }
        return false;
      })
    );
    
    return currentUserFires;
  } catch (error) {
    console.error('Error getting fires assigned to current user:', error);
    throw error;
  }
};

// Get fires by account ID
export const getFiresByAccount = async (accountId) => {
  try {
    const firesRef = collection(db, FIRES_COLLECTION);
    const q = query(firesRef, where('accountId', '==', accountId), orderBy('dateReported', 'desc'));
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Error getting fires by account:', error);
    throw error;
  }
};

// Get fires by actual account ID (for CRM view) - fires created by users from this account
export const getFiresByActualAccountId = async (accountId) => {
  try {
    // First, get all users associated with this account
    const usersRef = collection(db, 'users');
    const usersQuery = query(usersRef, where('accountId', '==', accountId));
    const usersSnapshot = await getDocs(usersQuery);
    
    const userIds = usersSnapshot.docs.map(doc => doc.id);
    
    if (userIds.length === 0) {
      return [];
    }
    
    // Query for fires where any of these users are the createdBy
    const firesRef = collection(db, FIRES_COLLECTION);
    const firesPromises = userIds.map(userId => {
      const q = query(
        firesRef,
        where('createdBy', '==', userId)
      );
      return getDocs(q);
    });
    
    const firesSnapshots = await Promise.all(firesPromises);
    
    // Combine and deduplicate fires
    const allFires = [];
    firesSnapshots.forEach(snapshot => {
      const fires = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      allFires.push(...fires);
    });
    
    // Remove duplicates based on fire ID
    const uniqueFires = allFires.filter((fire, index, self) => 
      index === self.findIndex(f => f.id === fire.id)
    );
    
    // Sort by date reported
    return uniqueFires.sort((a, b) => {
      if (!a.dateReported && !b.dateReported) return 0;
      if (!a.dateReported) return 1;
      if (!b.dateReported) return -1;
      return new Date(b.dateReported) - new Date(a.dateReported); // Descending order
    });
    
  } catch (error) {
    console.error('Error getting fires by actual account ID:', error);
    throw error;
  }
};

// Fires a portal user reported themselves (dashboard "pending fires" count)
export const getFiresCreatedBy = async (userId) => {
  const q = query(collection(db, FIRES_COLLECTION), where('createdBy', '==', userId));
  const querySnapshot = await getDocs(q);
  return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
};

// Subscribe to fires changes. Portal callers pass their accountId: clients may only
// read their own company's fires (firestore.rules), so the filter is in the query.
export const subscribeToFires = (callback, accountId = null) => {
  try {
    const firesRef = collection(db, FIRES_COLLECTION);
    const q = accountId
      ? query(firesRef, where('accountId', '==', accountId), orderBy('dateReported', 'desc'))
      : query(firesRef, orderBy('dateReported', 'desc'));
    
    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      const fires = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      callback(fires);
    });
    
    return unsubscribe;
  } catch (error) {
    console.error('Error subscribing to fires:', error);
    throw error;
  }
}; 